import { Component, Input, Output, EventEmitter, OnInit, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LegalService } from '../../../service/legal.service';
import { Elate } from '../../../model/elate.model';
import { LegalDocument } from '../../../model/legal.model';

/**
 * Composant d'acceptation des CGU pour FlashLevel (Elate).
 * Utilise l'API externe (sans auth, par token Elate).
 *
 * Usage :
 * <lib-ai-exam-onboarding-legal
 *   [elate]="elate"
 *   (legalAccepted)="onLegalAccepted()">
 * </lib-ai-exam-onboarding-legal>
 */
@Component({
  selector: 'lib-ai-exam-onboarding-legal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    TranslateModule,
  ],
  templateUrl: './AI-exam-onboarding-legal.component.html',
  styleUrls: ['./AI-exam-onboarding-legal.component.scss'],
})
export class AIExamOnboardingLegalComponent implements OnInit {
  @Input() elate!: Elate;
  @Output() back = new EventEmitter<void>();
  @Output() legalAccepted = new EventEmitter<void>();

  private legalService = inject(LegalService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);

  form = new FormGroup({
    termsAccepted: new FormControl(false, Validators.requiredTrue),
    honorAccepted: new FormControl(false, Validators.requiredTrue),
  });

  cguDocument = signal<LegalDocument | null>(null);
  honorDocument = signal<LegalDocument | null>(null);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  private alreadyAcceptedDocIds = new Set<number>();

  ngOnInit(): void {
    this.loadLegalDocuments();
  }

  private loadLegalDocuments(): void {
    if (!this.elate?.token) {
      return;
    }

    this.isLoading.set(true);

    this.legalService.getLegalDocumentsForFlashLevel(this.elate.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (docs) => {
          this.cguDocument.set(docs.find(doc => doc.document_type === 'flashlevel_terms_of_service') || null);
          this.honorDocument.set(docs.find(doc => doc.document_type === 'flashlevel_honor_declaration') || null);

          this.legalService.getLegalAcceptanceStatusForFlashLevel(this.elate.token)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (status) => {
                this.alreadyAcceptedDocIds = new Set(status.accepted_document_ids);

                const cgu = this.cguDocument();
                const honor = this.honorDocument();
                if (cgu && this.alreadyAcceptedDocIds.has(cgu.id)) {
                  this.form.get('termsAccepted')?.setValue(true);
                }
                if (honor && this.alreadyAcceptedDocIds.has(honor.id)) {
                  this.form.get('honorAccepted')?.setValue(true);
                }

                this.isLoading.set(false);
              },
              error: () => {
                this.isLoading.set(false);
              }
            });
        },
        error: () => {
          this.errorMessage.set(this.translateService.instant('ai-exam-onboarding.legal.error-load'));
          this.isLoading.set(false);
        }
      });
  }

  toggleCheckbox(name: string): void {
    const control = this.form.get(name);
    if (control) {
      control.setValue(!control.value);
    }
  }

  async onConfirm(): Promise<void> {
    if (!this.form.valid || !this.elate?.token) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      // Acceptation CGU (terms_of_service)
      if (this.cguDocument() && this.cguDocument()!.content) {
        if (!this.alreadyAcceptedDocIds.has(this.cguDocument()!.id)) {
          const contentHash = await this.calculateSHA256(this.cguDocument()!.content);
          await this.legalService.acceptDocumentForFlashLevel(this.elate.token, {
            legal_document_id: this.cguDocument()!.id,
            document_version: this.cguDocument()!.version,
            content_hash: contentHash
          }).pipe(takeUntilDestroyed(this.destroyRef)).toPromise();
        }
      }

      // Acceptation déclaration sur l'honneur (honor_declaration)
      if (this.honorDocument() && this.honorDocument()!.content) {
        if (!this.alreadyAcceptedDocIds.has(this.honorDocument()!.id)) {
          const contentHash = await this.calculateSHA256(this.honorDocument()!.content);
          await this.legalService.acceptDocumentForFlashLevel(this.elate.token, {
            legal_document_id: this.honorDocument()!.id,
            document_version: this.honorDocument()!.version,
            content_hash: contentHash
          }).pipe(takeUntilDestroyed(this.destroyRef)).toPromise();
        }
      }

      this.isLoading.set(false);
      this.legalAccepted.emit();
    } catch (error) {
      this.errorMessage.set(this.translateService.instant('ai-exam-onboarding.legal.error-save'));
      this.isLoading.set(false);
    }
  }

  private async calculateSHA256(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  openCguModal(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const document = this.cguDocument();
    if (!document) {
      return;
    }

    this.dialog.open(FlashLevelLegalDocumentDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: document.title,
        content: document.content,
        version: document.version,
      }
    });
  }
}

/**
 * Dialog pour afficher un document légal FlashLevel
 */
@Component({
  selector: 'lib-flashlevel-legal-document-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
  ],
  template: `
    <div class="flex flex-col h-full">
      <div class="flex items-center justify-between p-6 border-b border-slate-200">
        <div>
          <h2 class="text-2xl font-semibold text-slate-900">{{ data.title }}</h2>
          <p class="text-sm text-slate-600 mt-1">Version {{ data.version }}</p>
        </div>
        <button mat-icon-button mat-dialog-close>
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="flex-auto overflow-y-auto p-6">
        <div class="prose prose-slate max-w-none">
          <div class="text-slate-800 whitespace-pre-wrap" [innerHTML]="data.content"></div>
        </div>
      </div>
      <div class="flex justify-end gap-3 p-6 border-t border-slate-200">
        <button mat-flat-button color="primary" mat-dialog-close translate>
          <span translate>ai-exam-onboarding.legal.close</span>
        </button>
      </div>
    </div>
  `,
  styles: [`:host { display: block; height: 100%; }`]
})
export class FlashLevelLegalDocumentDialogComponent {
  data = inject<{ title: string; content: string; version: string }>(MAT_DIALOG_DATA);
}
